import { useQueries } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import type { Board, Org } from '../../lib/api';
import { Button, Dialog, Select } from '../../ui';
import { settle } from './bulk';
import { inverseOf } from './model/actions';
import { qk, useInbox, useProjects } from './queries';
import { useUndoable } from './undo';

/**
 * Moves boards to a place with one Undo (P16-4): boards already there are left alone, partial failures are reported,
 * and nothing at all happens (no toast) when there is nothing to move. Rejects when every move failed.
 */
export function useMoveBoards() {
  const { t } = useTranslation();
  const api = useApi();
  const undoable = useUndoable();
  return async (boards: readonly Board[], to: { projectId: string; folderId: string | null }) => {
    const moving = boards.filter((b) => b.projectId !== to.projectId || b.folderId !== to.folderId);
    if (moving.length === 0) return;
    const { done } = await settle(
      moving.map((b) => b.id),
      (id) => api.updateBoard(id, to),
    );
    if (done.length === 0) throw new Error('move_failed');
    const inverses = moving.filter((b) => done.includes(b.id)).map((b) => inverseOf('move', b)!);
    const message =
      done.length < moving.length
        ? t('dashboard.bulk.partial.move', { done: done.length, total: moving.length })
        : boards.length === 1
          ? t('dashboard.done.move')
          : t('dashboard.bulk.done.move', { count: done.length });
    await undoable(async () => {}, inverses, message);
  };
}

/** "Move to…": every project and folder of the organization, for one board or a selection; one Undo (P16-4). */
export function MoveBoardDialog({ boards, org, onClose }: { boards: readonly Board[]; org: Org; onClose: () => void }) {
  const board = boards[0]!;
  const { t } = useTranslation();
  const api = useApi();
  const move = useMoveBoards();
  const projects = useProjects(org.id);
  const inbox = useInbox(org.id);
  const folders = useQueries({
    queries: (projects.data ?? []).map((p) => ({ queryKey: qk.folders(p.id), queryFn: () => api.listFolders(p.id) })),
  });
  // « Sans projet » first, outside the alphabetical order: it has no folders, and it is where loose boards go (P17).
  const options = [
    ...(inbox.data ? [{ value: `${inbox.data}:`, label: t('dashboard.noProject') }] : []),
    ...(projects.data ?? [])
      .flatMap((p, i) => [
        { value: `${p.id}:`, label: p.name },
        ...(folders[i]?.data ?? []).map((f) => ({ value: `${p.id}:${f.id}`, label: `${p.name} / ${f.name}` })),
      ])
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const [target, setTarget] = useState(`${board.projectId}:${board.folderId ?? ''}`);
  const [error, setError] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const [projectId, folderId] = target.split(':');
    try {
      await move(boards, { projectId: projectId!, folderId: folderId || null });
    } catch {
      return setError(true);
    }
    onClose();
  };

  return (
    <Dialog title={t('dashboard.moveBoard')} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('dashboard.destination')}
          <Select value={target} onChange={(e) => setTarget(e.target.value)}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
        {error && (
          <p role="alert" className="font-sans text-xs text-danger">
            {t('auth.genericError')}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>
            {t('dashboard.cancel')}
          </Button>
          <Button variant="primary" type="submit">
            {t('dashboard.move')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
